import QueryString from 'qs';
import {
  Collection,
  Header,
  PopoverContext,
  Menu as RACMenu,
  MenuItem as RACMenuItem,
  type MenuItemProps as RACMenuItemProps,
  type MenuProps as RACMenuProps,
  Section,
  type SectionProps,
  Separator,
  composeRenderProps,
  useSlottedContext,
} from 'react-aria-components';
import {
  MenuTrigger as AriaMenuTrigger,
  SubmenuTrigger as AriaSubmenuTrigger,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { Button, type ButtonWithoutAsChildProps } from './button';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from './icons';
import { Popover, type PopoverProps } from './popover';
import { Small } from './text';
import { composeTailwindRenderProps } from './utils';

export const MenuTrigger = AriaMenuTrigger;
export const SubmenuTrigger = AriaSubmenuTrigger;

type MenuButtonProps = ButtonWithoutAsChildProps & {
  noIndicator?: boolean;
};

const MENUHREFBASEOPTS = {
  arrayFormat: 'brackets',
  sort: (a: any, b: any) => `${a || ''}`.localeCompare(`${b || ''}`, 'en'),
} as const;

export function menuHref(
  base: any,
  rest: any,
  options?: QueryString.IStringifyOptions<QueryString.BooleanOptional>,
) {
  const str = QueryString.stringify(
    { ...base, ...rest },
    options ? { ...MENUHREFBASEOPTS, ...options } : MENUHREFBASEOPTS,
  );

  return str ? `?${str}` : '';
}

export function MenuButton({
  className,
  noIndicator,
  variant,
  children,
  ...props
}: MenuButtonProps) {
  // Set`outline` as the default button style
  if (!variant) {
    variant = 'outline';
  }

  return (
    <Button
      {...props}
      variant={variant}
      className={composeTailwindRenderProps(className, [
        'gap-1',
        'pressed:scale-[0.97] pressed:opacity-75',
        'group-data-[ui=button-group]:pressed:scale-100',
        'group-data-[ui=button-group]:pressed:opacity-100',
        variant === 'unstyle' ? '' : 'px-2.5',
      ])}
    >
      {(renderProps) => {
        return (
          <>
            {typeof children === 'function' ? children(renderProps) : children}
            {!noIndicator && (
              <ChevronDownIcon
                className={twMerge(!!children && 'ms-1', 'in-[.justify-start]:ms-auto')}
              />
            )}
          </>
        );
      }}
    </Button>
  );
}

export function MenuPopover({ className, placement, ...props }: PopoverProps) {
  const popoverContext = useSlottedContext(PopoverContext)!;
  const isSubmenu = popoverContext?.trigger === 'SubmenuTrigger';

  return (
    <Popover
      {...props}
      placement={placement ?? (isSubmenu ? undefined : 'bottom')}
      className={composeTailwindRenderProps(
        className,
        twMerge(
          'dark:bg-zinc-800',
          'dark:ring-zinc-700',
          'max-w-72',
          'rounded-xl',
          'min-w-[max(112px,var(--trigger-width))]',
          'has-[[data-ui=item]_[data-ui=icon]]:min-w-[max(200px,var(--trigger-width))]',
          'has-[[data-ui=item]_kbd]:min-w-[max(176px,var(--trigger-width))]',
        ),
      )}
    />
  );
}
export function Menu<T extends object>(props: RACMenuProps<T>) {
  return (
    <RACMenu
      {...props}
      className={twMerge(
        'flex max-h-[inherit] flex-col overflow-auto outline-hidden',
        'p-1 has-[header]:px-2 has-[header]:pt-0',

        // MeuItem style
        '**:data-[ui=item]:flex-1',
        '**:data-[ui=item]:grid',
        '[&_[data-ui=item]:has([data-ui=label])]:grid-cols-[16px_1fr_minmax(50px,max-content)]',
        '**:data-[ui=item]:items-center',
        '**:data-[ui=item]:gap-x-2',

        // icon
        '[&_[data-ui=item]>[data-ui=icon]:not([class*=text-])]:text-muted',
        '[&_[data-ui=item][data-destructive]>[data-ui=icon]]:text-destructive/75',
        '[&:has([data-ui=icon]+[data-ui=label])_[data-ui=item]>[data-ui=icon]:not([class*=size-])]:size-4',
        '[&:has([data-ui=icon]+[data-ui=label])_[data-ui=item]>[data-ui=icon]]:col-start-1',

        '[&_[data-ui=item]>[data-ui=label]]:col-span-full',
        '[&:has([data-ui=icon]+[data-ui=label])_[data-ui=item]>[data-ui=label]]:col-start-2',

        // kbd
        '[&:has([data-ui=kbd])_[data-ui=item]>[data-ui=label]]:col-end-[-2]',
        '[&_[data-ui=item]>[data-ui=kbd]]:col-span-1',
        '[&_[data-ui=item]>[data-ui=kbd]]:row-start-1',
        '[&_[data-ui=item]>[data-ui=kbd]]:col-start-3',
        '[&_[data-ui=item]>[data-ui=kbd]]:justify-self-end',
        '[&_[data-ui=item][data-destructive]>[data-ui=kbd]]:text-destructive',

        // Description
        '[&_[data-ui=item]>[data-ui=description]]:col-span-full',
        '[&:has([data-ui=kbd])_[data-ui=item]>[data-ui=description]]:col-end-[-2]',
        '[&:has([data-ui=icon]+[data-ui=label]+[data-ui=description])_[data-ui=item]>[data-ui=description]]:col-start-2',
        props.className,
      )}
    />
  );
}

export function SubMenu<T extends object>(props: RACMenuProps<T> & { 'aria-label': string }) {
  return <Menu {...props} />;
}

export function MenuSeparator({ className }: { className?: string }) {
  return (
    <Separator
      className={twMerge(
        'my-1 w-[calc(100%-24px)] self-center border-zinc-950/5 border-t dark:border-white/10',
        className,
      )}
    />
  );
}

type MenuItemProps = RACMenuItemProps & {
  destructive?: true;
};

export function MenuItem({ destructive, ...props }: MenuItemProps) {
  return (
    <RACMenuItem
      {...props}
      className={composeRenderProps(props.className, (className) => {
        return twMerge([
          'group flex cursor-default select-none items-center gap-x-2 outline-hidden',
          'px-3 py-2.5 sm:py-1.5 data-selection-mode:px-2',
          'rounded-sm',
          'text-base/6 sm:text-sm/6',
          'disabled:opacity-50',
          'focus:bg-zinc-100 dark:focus:bg-zinc-700',
          destructive && 'text-destructive',
          className,
        ]);
      })}
    >
      {composeRenderProps(props.children, (children, { selectionMode, isHovered }) => (
        <>
          {selectionMode !== 'none' && (
            <span
              data-ui="icon"
              className={twMerge('flex w-4 shrink-0 self-start', 'group-selected:mt-[5px]')}
            >
              <CheckIcon height={16} className="hidden size-4 group-selected:inline-block" />
            </span>
          )}

          <div
            data-ui="item"
            data-destructive={destructive ? destructive : undefined}
            data-hovered={isHovered}
          >
            {children}
          </div>
          {/* Submenu indicator */}
          <ChevronRightIcon className="-me-2 hidden size-4 text-muted group-data-[has-submenu]:inline-block" />
        </>
      ))}
    </RACMenuItem>
  );
}

export function MenuItemLabel({ className, ...props }: JSX.IntrinsicElements['span']) {
  return (
    <span slot="label" data-ui="label" className={twMerge('mb-0 truncate', className)} {...props} />
  );
}

export function MenuItemDescription({ className, ...props }: JSX.IntrinsicElements['span']) {
  return <Small slot="description" data-ui="description" className={className} {...props} />;
}

export interface MenuSectionProps<T> extends SectionProps<T> {
  title?: string;
}

export function MenuSection<T extends object>({ className, ...props }: MenuSectionProps<T>) {
  return (
    <Section
      className={twMerge(
        'first:-mt-[1px]',
        'not-first:my-1.5',
        'not-first:border-t not-first:border-t-border/40',
        // Header
        // selectionMode = none
        '[&_header]:px-3',
        // has icon
        '[&:has([data-ui=item]>[data-ui=icon]+[data-ui=label])_header]:px-4',
        // electionMode !== none
        '[&:has([role=menuitemradio])_header]:ps-8',
        // has icon
        '[&:has([role=menuitemradio]>[data-ui=item]>[data-ui=icon]+[data-ui=label])_header]:ps-9',
        className,
      )}
    >
      <Header
        className={twMerge(
          'sticky z-10 bg-white dark:bg-zinc-800 truncate pt-2 text-xs/4 text-muted',
          'top-[0px] -mx-[1px] rounded-sm backdrop-blur-md',
        )}
      >
        {props.title}
      </Header>
      <Collection items={props.items}>{props.children}</Collection>
    </Section>
  );
}
